import * as React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { TreeDto, TreeListDto } from '@lib/dto/tree.dto'
import type { TreeSyncOperation } from '@lib/input/tree.input'
import { treeService } from '@/services/TreeService'
import { treeKeys } from '@/hooks/queryKeys'
import { useTrees } from '@/hooks/useTrees'
import {
  applyQueue,
  enqueue,
  inVisit,
  loadCache,
  operationTreeId,
  saveCache,
  withQueue,
  withServerFindings,
  withTrees,
  EMPTY_CACHE,
  type LoadedCache,
  type SurveyCache,
} from '@/field-map/treeOutbox'
import { treeSyncOperationSchema } from '@lib/input/tree.input'
import { meetsItemizationRule } from '@lib/helpers/job-time'
import { toErrorMessage } from '@/helpers/logger'
import { ApiError } from '@/helpers/apiClient'
import { isRetriableStatus } from '@/helpers/retriableStatus'
import {
  isForVisit,
  type SurveyVisit,
  type SyncFailure,
} from '@/site-visits/surveyVisit'

export type { SyncFailure } from '@/site-visits/surveyVisit'

/** Nothing loaded, nothing lost — the state for "no site selected yet". */
const EMPTY_LOAD: LoadedCache = { cache: EMPTY_CACHE, lostOperations: 0 }

/**
 * Whether the server would accept this operation.
 *
 * The shared schema rather than a second opinion about it, so the two ends
 * cannot drift into disagreeing about what is acceptable.
 */
function isSyncable(
  operation: TreeSyncOperation,
  requireItemizedEstimates: boolean,
): boolean {
  if (!treeSyncOperationSchema.safeParse(operation).success) return false
  /*
   * The operator's rule, checked here as well as at the API. The server
   * refuses an un-itemized tree when this is on, and a refused operation
   * fails every other queued edit for the site with it — so the device has to
   * know the rule rather than find out by having the batch rejected.
   */
  if (operation.op !== 'upsert') return true
  return meetsItemizationRule(
    (operation.tree.jobs ?? []).map((job) => ({
      type: job.type,
      minutes: job.minutes ?? null,
    })),
    requireItemizedEstimates,
  )
}

/** Coalesce a burst of edits into one request instead of one per keystroke. */
const FLUSH_DEBOUNCE_MS = 800

/**
 * Fallback retry while anything is queued. The `online` event handles the
 * common case; this catches the one it misses — a phone that reports itself
 * online while sitting behind a captive portal or a dead cell, where the
 * browser never fires an event because nothing about the interface changed.
 */
const FLUSH_RETRY_MS = 20_000

/**
 * Retire a flush that finished after the survey had moved on to another site.
 *
 * The screen's working set now belongs to the site on screen, so this result
 * must not be written there. The site that was sent still has its queue in
 * storage, and leaving the claimed edits there would replay them forever.
 * Drop what this flush claimed, and take the server's copy when that cache
 * is still showing the visit that was asked.
 */
function retirePersistedFlush(
  siteId: string,
  highWaterMark: number,
  result: TreeListDto,
  asked: SurveyVisit | null,
): void {
  const cache = loadCache(siteId).cache
  const remaining = cache.queue.filter((queued) => queued.seq > highWaterMark)
  const cacheVisit = cache.visitId
  const stillThatVisit =
    asked === null
      ? cacheVisit === null || cacheVisit === undefined
      : cacheVisit === asked.id ||
        (cacheVisit != null && asked.joinedIds.includes(cacheVisit))
  if (stillThatVisit && isForVisit(result.visitId, asked)) {
    saveCache(
      siteId,
      withServerFindings(
        cache,
        applyQueue(result.trees, remaining, cache.trees, asked),
        result.visitId,
        remaining,
      ),
    )
    return
  }
  saveCache(siteId, withQueue(cache, remaining))
}

/*
 * For trees, `needsAttention` on a SyncFailure is a refusal the retry loop
 * must not paper over. The survey endpoint rejects a whole batch when a
 * label collides with a live tree on the site — which is a real thing to do
 * offline, starting a fresh device at #1 on a site that already has one —
 * and every retry after that is the same refusal.
 */
export interface TreeSurvey {
  /** The working set: server truth with any unflushed local edits applied. */
  trees: TreeDto[]
  /** Operations recorded here that the server has not accepted yet. */
  pendingCount: number
  /**
   * Whether the trees' findings are known to be the working visit's, so they
   * can be edited. False while a visit's findings are loading, or before a
   * cache from an older build has been checked against the server.
   */
  findingsReady: boolean
  /**
   * Trees the server would refuse as they stand, held out of the queue.
   *
   * Not a network problem: a person has to finish the tree — describe an
   * "Other" problem, say what an "Other" job covers, complete a half-split.
   * Kept separate from `pendingTreeIds` because the remedy is different.
   */
  heldTreeIds: string[]
  /**
   * Trees with queued operations — which is to say, trees the server may not
   * have heard of yet.
   *
   * Anything keyed to a tree's server-side existence has to wait for this to
   * clear. Conservative on purpose: a tree with a pending EDIT does exist, but
   * telling the two cases apart would mean tracking which ids have ever been
   * acknowledged, and being briefly over-strict costs a second of waiting
   * where being wrong costs a failed upload in a park.
   */
  pendingTreeIds: ReadonlySet<string>
  isSyncing: boolean
  /**
   * Queued work this build could not read back, and so can never replay.
   *
   * Surfaced rather than swallowed because the alternative is the worst thing
   * this screen can do: show a tick. A surveyor reads "Saved" and walks back to
   * the truck, and the morning is gone with no one aware of it.
   */
  lostOperations: number
  /** Why the last flush failed, or null if the last one worked. */
  syncError: SyncFailure | null
  isOnline: boolean
  /**
   * With the answer to "Was it wrong, or did it change?", when one was
   * asked, and the day it changed when that was asked too.
   */
  upsertTree: (
    tree: TreeDto,
    isCorrection?: boolean,
    changedOn?: string,
  ) => void
  archiveTree: (id: string, isCorrection?: boolean, changedOn?: string) => void
  flush: () => void
}

/**
 * Reduce a full tree record to the write payload the sync endpoint takes,
 * for the visit its findings go into (null: none open, so the tree only).
 */
export function toUpsertOperation(
  tree: TreeDto,
  visitId: string | null,
): TreeSyncOperation {
  return {
    op: 'upsert',
    visitId,
    tree: {
      id: tree.id,
      label: tree.label,
      speciesId: tree.speciesId,
      problems: tree.problems.map((problem) => ({
        id: problem.id,
        typeId: problem.typeId,
        notes: problem.notes,
        severity: problem.severity,
        season: problem.season,
        // The quantities only; the server recomputes the money, because it
        // owns the crew rate and this tree's diameter.
        solutions: problem.solutions.map((solution) => ({
          solutionId: solution.solutionId,
          minutes: solution.minutes,
          linearFeet: solution.linearFeet,
          season: solution.season,
        })),
        previousId: problem.previousId,
        priceOverride: problem.priceOverride,
      })),
      latitude: tree.latitude,
      longitude: tree.longitude,
      accuracyM: tree.accuracyM,
      positionSource: tree.positionSource,
      estimatedLaborMinutes: tree.estimatedLaborMinutes,
      dbhInches: tree.dbhInches,
      removalOffered: tree.removalOffered,
      removalMinutes: tree.removalMinutes,
      removalNotes: tree.removalNotes,
      noPruningRecommended: tree.noPruningRecommended,
      pruningPriceOverride: tree.pruningPriceOverride,
      removalPriceOverride: tree.removalPriceOverride,
      priceOverrideNotes: tree.priceOverrideNotes,
      isOptional: tree.isOptional,
      notes: tree.notes,
      jobs: tree.jobs,
      pruningSeason: tree.pruningSeason,
    },
  }
}

/**
 * A site survey: the local working set, the queue of changes owed to the
 * server, and the machinery that drains it.
 *
 * The division of labor is the point. Local state is authoritative for what
 * the map draws, so editing never waits on a request and never fails because a
 * radio is out. The server is authoritative for what exists, so every flush
 * replaces the working set with what came back — which is how a change made
 * from a desk reaches a phone that has been offline all morning.
 */
export function useTreeSurvey(
  siteId: string,
  /**
   * The operator's rules, which the device has to know rather than discover
   * by having a batch refused.
   */
  {
    requireItemizedEstimates = false,
    visit = null,
    canRecord = visit !== null,
    canSend = true,
  }: {
    requireItemizedEstimates?: boolean
    /**
     * The visit being worked in: what the trees' findings show and where an
     * edit's findings go. Null when the site has none.
     */
    visit?: SurveyVisit | null
    /**
     * Whether findings can be written into `visit`. False shows its findings
     * read-only: an edit then writes the tree itself and names no visit, so
     * changing a label a year later cannot rewrite last year's findings.
     */
    canRecord?: boolean
    /**
     * False while a visit started on this phone has not reached the server.
     * Nothing is sent until it has, since the server refuses changes naming
     * a visit it has never heard of.
     */
    canSend?: boolean
  } = {},
): TreeSurvey {
  const queryClient = useQueryClient()
  const visitId = visit?.id ?? null
  // Asked for only once the visit has reached the server: before, it would
  // answer that there is no such visit, and not be asked again.
  const query = useTrees(siteId, visitId, canSend)

  const [loaded, setLoaded] = React.useState(() =>
    siteId === '' ? EMPTY_LOAD : loadCache(siteId),
  )
  const [cache, setCache] = React.useState<SurveyCache>(() => loaded.cache)
  // The site `cache` was loaded for. A render where the route has already
  // changed still holds the previous site's trees until the effect below
  // replaces them, and a flush must not treat that mix as the new site.
  const [cacheSiteId, setCacheSiteId] = React.useState(siteId)
  const [syncError, setSyncError] = React.useState<SyncFailure | null>(null)
  const [isOnline, setIsOnline] = React.useState(() => navigator.onLine)

  // Read by the flush closure, which must never see a stale queue.
  const cacheRef = React.useRef(cache)
  cacheRef.current = cache
  const flushingRef = React.useRef(false)
  const onlineRef = React.useRef(isOnline)
  onlineRef.current = isOnline
  const visitRef = React.useRef(visit)
  visitRef.current = visit
  const siteIdRef = React.useRef(siteId)
  siteIdRef.current = siteId
  const cacheReadyRef = React.useRef(false)
  cacheReadyRef.current = cacheSiteId === siteId
  const mountedRef = React.useRef(true)
  const canSendRef = React.useRef(canSend)
  canSendRef.current = canSend
  const canRecordRef = React.useRef(canRecord)
  // Recording also needs the view to be this visit's own: see `fresh`.
  canRecordRef.current =
    canRecord &&
    cache.fresh === true &&
    visit !== null &&
    cache.visitId === visit.id

  const syncMutation = useMutation({
    mutationFn: ({
      operations,
      visitId: shown,
    }: {
      operations: TreeSyncOperation[]
      visitId: string | null
    }) => treeService.sync(siteId, operations, shown),
  })
  // Held in a ref so `flush` can stay referentially stable: it is called from
  // effects and from event listeners that should not be torn down and rebuilt
  // every time the mutation's internal state changes.
  const syncRef = React.useRef(syncMutation)
  syncRef.current = syncMutation

  /* -- swap in a new site ------------------------------------------- */

  React.useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  React.useEffect(() => {
    const next = siteId === '' ? EMPTY_LOAD : loadCache(siteId)
    setLoaded(next)
    setCache(next.cache)
    setCacheSiteId(siteId)
    setSyncError(null)
  }, [siteId])

  /* -- persist ------------------------------------------------------- */

  React.useEffect(() => {
    // The render that first sees a new site still holds the previous site's
    // cache. Writing that under the new id would replace the site just
    // opened with the one just left.
    if (siteId === '' || cacheSiteId !== siteId) return
    saveCache(siteId, cache)
  }, [siteId, cache, cacheSiteId])

  /* -- the visit being worked in ------------------------------------- */

  // A different visit shows its own findings: nothing, until the server's
  // copy of it arrives. The same visit under its kept id is only relabeled.
  const visitKey = visit === null ? '' : [visit.id, ...visit.joinedIds].join(',')
  React.useEffect(() => {
    setCache((current) =>
      inVisit(current, visitRef.current),
    )
  }, [visitKey])

  /* -- fold server truth in ------------------------------------------ */

  React.useEffect(() => {
    const data = query.data
    if (data === undefined || !isForVisit(data.visitId, visitRef.current)) return
    setCache((current) =>
      // The server's copy of the visit: what is shown is now its own. A
      // cache from before visits is named here, because `fresh` is only
      // meaningful once a visit has been chosen.
      withServerFindings(
        current,
        applyQueue(data.trees, current.queue, current.trees, visitRef.current),
        data.visitId,
      ),
    )
  }, [query.data])

  /* -- online / offline ---------------------------------------------- */

  React.useEffect(() => {
    function goOnline(): void {
      setIsOnline(true)
    }
    function goOffline(): void {
      setIsOnline(false)
    }
    window.addEventListener('online', goOnline)
    window.addEventListener('offline', goOffline)
    return () => {
      window.removeEventListener('online', goOnline)
      window.removeEventListener('offline', goOffline)
    }
  }, [])

  /* -- flushing ------------------------------------------------------ */

  const flush = React.useCallback((): void => {
    if (siteId === '' || flushingRef.current) return
    // The cache state is still the previous site's until its load commits.
    if (!cacheReadyRef.current) return

    const pending = cacheRef.current.queue
    if (pending.length === 0) return

    /*
     * Don't dial out on a radio the browser already says is dead. The request
     * would achieve nothing, and it would flip the status to "Saving" for as
     * long as it took to fail — which reads as progress to someone who needs to
     * know their morning's work is still only on the phone. The `online`
     * listener starts a flush the moment that changes.
     *
     * Note the asymmetry: `navigator.onLine === false` is trustworthy, while
     * `true` only means an interface is up — a captive portal or a dead cell
     * still reports online. That is what the retry interval is for.
     */
    if (!onlineRef.current) return
    if (!canSendRef.current) return

    /*
     * Claim everything queued as of now. Operations recorded while the request
     * is in flight get higher sequence numbers and are deliberately left
     * behind, so a slow network cannot swallow an edit made during it.
     */
    const highWaterMark = pending.reduce(
      (max, queued) => Math.max(max, queued.seq),
      0,
    )
    const operations = pending.map((queued) => queued.operation)
    // Captured here, not read again after the await. The visit and the site
    // on screen may both have changed while the request was out, and the
    // result belongs to the ones that were asked.
    const asked = visitRef.current
    const flushSite = siteId

    flushingRef.current = true
    void (async (): Promise<void> => {
      try {
        const result = await syncRef.current.mutateAsync({
          operations,
          visitId: asked?.id ?? null,
        })
        if (siteIdRef.current !== flushSite) {
          // The page is reused when only the site id changes, so this
          // component is now drawing a different site. Writing the result
          // into state would file site A's trees under site B, and the next
          // persist would save them there.
          if (isForVisit(result.visitId, asked)) {
            queryClient.setQueryData(
              treeKeys.list(flushSite, asked?.id ?? null),
              result,
            )
          }
          retirePersistedFlush(flushSite, highWaterMark, result, asked)
          return
        }
        setSyncError(null)
        // Taken as the working set only if the visit has not changed while
        // the request was out; the queue is retired either way.
        const current = isForVisit(result.visitId, visitRef.current)
        setCache((cached) => {
          const remaining = cached.queue.filter(
            (queued) => queued.seq > highWaterMark,
          )
          if (!current) return withQueue(cached, remaining)
          return withServerFindings(
            cached,
            applyQueue(result.trees, remaining, cached.trees, visitRef.current),
            asked?.id ?? null,
            remaining,
          )
        })
        if (current) {
          queryClient.setQueryData(
            treeKeys.list(flushSite, asked?.id ?? null),
            result,
          )
        }
      } catch (caught) {
        /*
         * The queue is untouched, so a failure defers work rather than losing
         * it. The retry loop will come back for it — which is the right answer
         * for a dropped connection and the wrong one for a refusal, so record
         * which this was rather than just that it happened.
         *
         * A failure for a site that is no longer on screen is that site's,
         * and it is still queued there. The site now open must not show it.
         */
        if (siteIdRef.current !== flushSite) return
        setSyncError({
          message: toErrorMessage(caught),
          needsAttention:
            caught instanceof ApiError && !isRetriableStatus(caught.status),
        })
      } finally {
        flushingRef.current = false
        if (siteIdRef.current !== flushSite) {
          // The new site's own flush may have been skipped while this one
          // held the lock, and it will not try again until the slow retry.
          // Wait until that site's cache has replaced this one: flushing
          // sooner would send this site's queue to the other site.
          const kick = (attempt: number): void => {
            if (!mountedRef.current) return
            if (!cacheReadyRef.current) {
              if (attempt >= 10) return
              window.setTimeout(() => {
                kick(attempt + 1)
              }, 0)
              return
            }
            flushRef.current()
          }
          window.setTimeout(() => {
            kick(0)
          }, 0)
        }
      }
    })()
  }, [siteId, queryClient])

  // The latest flush, so a request that outlives a site change can start
  // the site now on screen instead of the one it closed over.
  const flushRef = React.useRef(flush)
  flushRef.current = flush

  // Debounced after any local change, and as soon as a visit started here
  // reaches the server and lets what was found on it follow.
  React.useEffect(() => {
    if (cache.queue.length === 0) return undefined
    const timer = window.setTimeout(flush, FLUSH_DEBOUNCE_MS)
    return () => {
      window.clearTimeout(timer)
    }
  }, [cache.queue, canSend, flush])

  // Retry loop while anything is still owed.
  React.useEffect(() => {
    if (cache.queue.length === 0) return undefined
    const timer = window.setInterval(flush, FLUSH_RETRY_MS)
    return () => {
      window.clearInterval(timer)
    }
  }, [cache.queue.length, flush])

  // Signal came back — try immediately rather than waiting for the interval.
  React.useEffect(() => {
    if (isOnline) flush()
  }, [isOnline, flush])

  /* -- mutations ------------------------------------------------------ */

  /**
   * `isCorrection` is the answer to "Was it wrong, or did it change?" when
   * a revisit asked it of the change: true for "Missed last time".
   */
  const upsertTree = React.useCallback((tree: TreeDto, isCorrection?: boolean, changedOn?: string): void => {
    setCache((current) => {
      const trees = current.trees.some((candidate) => candidate.id === tree.id)
        ? current.trees.map((candidate) =>
            candidate.id === tree.id ? tree : candidate,
          )
        : [...current.trees, tree]

      const built = toUpsertOperation(
        tree,
        canRecordRef.current ? (visitRef.current?.id ?? null) : null,
      )
      const operation: TreeSyncOperation =
        isCorrection === undefined
          ? built
          : {
              ...built,
              isCorrection,
              ...(changedOn === undefined ? {} : { changedOn }),
            }

      /*
       * A tree the server will refuse never reaches the queue.
       *
       * The batch is validated whole, so ONE rejected operation fails every
       * other queued edit for the site with it — non-retriably, with the retry
       * loop replaying the same payload. A half-typed measurement used to do
       * this; so does an "Other" problem with no description, or an "Other"
       * job carrying minutes but no note, both of which the form invites
       * somebody to pass through on the way to a complete tree.
       *
       * The edit still lands in the working set, so nothing is lost and the
       * form keeps showing what was typed with its own red hint beside the
       * field. It simply is not offered to the server until it is acceptable,
       * and the sync indicator says a tree is waiting on a person.
       */
      if (!isSyncable(operation, requireItemizedEstimates)) {
        return withTrees(current, trees)
      }

      return withTrees(enqueue(current, operation, tree), trees)
    })
  }, [requireItemizedEstimates])

  /**
   * Take a tree off the site. `isCorrection` is the answer a revisit asks:
   * true for "Recorded by mistake", false for "Gone". Unanswered, it was a
   * mistake, which is what deleting meant before there was a choice.
   */
  const archiveTree = React.useCallback((id: string, isCorrection?: boolean, changedOn?: string): void => {
    setCache((current) =>
      withTrees(
        enqueue(current, {
          op: 'archive',
          id,
          visitId: canRecordRef.current ? (visitRef.current?.id ?? null) : null,
          ...(isCorrection === undefined ? {} : { isCorrection }),
          ...(changedOn === undefined ? {} : { changedOn }),
        }),
        current.trees.filter((tree) => tree.id !== id),
      ),
    )
  }, [])

  /*
   * Memoised, and that matters more than it looks.
   *
   * This object is a dependency of the effect that draws the map's pins. A
   * fresh literal every render meant that effect could never match its own
   * dependencies, so it re-ran on EVERY render of the survey screen — every
   * keystroke in the tree form, every GPS fix, every sync tick — rebuilding
   * all ninety-odd markers each time. Measured at 883 DOM mutations and 37ms
   * per keystroke on a desktop, which is several times that on the phone this
   * screen is actually used on.
   *
   * `pendingTreeIds` is built here rather than inline for the same reason: a
   * new Set every render is a new identity every render.
   */
  const pendingTreeIds = React.useMemo(
    () =>
      new Set(cache.queue.map((entry) => operationTreeId(entry.operation))),
    [cache.queue],
  )

  const findingsReady =
    visit !== null && cache.fresh === true && cache.visitId === visit.id

  /**
   * Trees the server would refuse as they stand.
   *
   * Derived rather than tracked, so it needs no place in the persisted outbox
   * and cannot fall out of step with the trees themselves. These are held back
   * from the queue, so without this the indicator would report everything
   * saved while an edit sat in the working set going nowhere.
   */
  const heldTreeIds = React.useMemo(
    () =>
      cache.trees
        .filter(
          (tree) =>
            !isSyncable(toUpsertOperation(tree, null), requireItemizedEstimates),
        )
        .map((tree) => tree.id),
    [cache.trees, requireItemizedEstimates],
  )

  return React.useMemo(
    () => ({
      trees: cache.trees,
      pendingCount: cache.queue.length,
      findingsReady,
      pendingTreeIds,
      /** Trees waiting on a person, not on the network. */
      heldTreeIds,
      isSyncing: syncMutation.isPending,
      lostOperations: loaded.lostOperations,
      syncError,
      isOnline,
      upsertTree,
      archiveTree,
      flush,
    }),
    [
      cache.trees,
      cache.queue.length,
      findingsReady,
      pendingTreeIds,
      heldTreeIds,
      syncMutation.isPending,
      loaded.lostOperations,
      syncError,
      isOnline,
      upsertTree,
      archiveTree,
      flush,
    ],
  )
}
